/**
 * Admin › Finanças › Ofertas de incentivo (W10-RIDER-ZONE-DISCOUNT, D490, ADR-0106; doc 17 section 3.4) - the pure view-model behind the screen where an operator
 * schedules a time-boxed incentive offer, a DIFFERENT operator approves it and either may end it early, over `@vello/api-client` `ActiveIncentivesAdminApi`.
 *
 *  - who may do what (`incentive_lever:view`, `:manage` = the maker and the kill, `:approve` = the checker; all GEO - bound to the market in the header scope);
 *  - whether THIS operator may decide an offer (never the one who proposed it - the server refuses `INCENTIVE_SELF_APPROVAL` anyway, the screen hides the button and says
 *    why) and whether it may be ended (only an offer in force or scheduled);
 *  - the draft checks of a proposal against the BOUNDS the governed lever policy publishes (the server enforces them; the screen never offers a value past them);
 *  - the closed windows an offer may run (hours) cut at the policy's longest window.
 * Nothing here names a country, a currency or an amount.
 */
import type { ActiveIncentiveState, ActiveIncentiveView, ActiveIncentivesListView } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

export type OfferTone = 'neutral' | 'success' | 'warning' | 'danger';

export function activeIncentiveAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean; readonly mayApprove: boolean } {
  if (grants === undefined) return { mayView: true, mayManage: true, mayApprove: true };
  return {
    mayView: grantsAllow(grants, { permission: 'incentive_lever:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'incentive_lever:manage', target: 'GEO' }),
    mayApprove: grantsAllow(grants, { permission: 'incentive_lever:approve', target: 'GEO' }),
  };
}

export function offerStateTone(state: ActiveIncentiveState): OfferTone {
  return state === 'IN_FORCE' ? 'success' : state === 'PROPOSED' || state === 'SCHEDULED' ? 'warning' : state === 'REFUSED' ? 'danger' : 'neutral';
}

export function policyStateTone(state: ActiveIncentivesListView['fund']['state']): OfferTone {
  return state === 'ACTIVE' ? 'success' : state === 'INVALID' ? 'danger' : state === 'DISABLED' ? 'warning' : 'neutral';
}

/** Why this operator may not approve an offer (or undefined when he may). */
export function offerApproveBlock(o: Pick<ActiveIncentiveView, 'status' | 'createdBy'>, operatorId: string | undefined, mayApprove: boolean, selfApproval = false): 'NOT_PENDING' | 'NO_PERMISSION' | 'OWN_PROPOSAL' | undefined {
  if (o.status !== 'PROPOSED') return 'NOT_PENDING';
  if (!mayApprove) return 'NO_PERMISSION';
  if (!selfApproval && operatorId !== undefined && o.createdBy.id === operatorId) return 'OWN_PROPOSAL';
  return undefined;
}

/** Only an approved offer that is in force or scheduled can be ended early (an expired or ended one has nothing to stop). */
export function offerCanEnd(o: Pick<ActiveIncentiveView, 'state'>, mayManage: boolean): boolean {
  return mayManage && (o.state === 'IN_FORCE' || o.state === 'SCHEDULED');
}

/** The windows (hours) an offer may run: a closed list, cut at the policy's longest window. */
export const OFFER_WINDOW_HOURS = [2, 6, 12, 24, 48, 72, 168, 336, 744] as const;
export function windowChoices(maxWindowHours: number): readonly number[] {
  return OFFER_WINDOW_HOURS.filter((h) => h <= maxWindowHours);
}

export type OfferDraftIssue = 'ZONE_REQUIRED' | 'DISCOUNT_REQUIRED' | 'DISCOUNT_OVER_POLICY' | 'PER_RIDE_CAP_REQUIRED' | 'PER_RIDE_CAP_OVER_POLICY' | 'SPEND_CAP_REQUIRED' | 'REASON_REQUIRED' | 'WINDOW_REQUIRED';

export interface OfferDraft {
  readonly zoneId: string;
  /** Discount in basis points (a percentage x 100); undefined until typed. */
  readonly discountBp: number | undefined;
  readonly perRideCapMinor: bigint | undefined;
  readonly spendCapMinor: bigint | undefined;
  readonly reasonCode: string;
  readonly windowHours: number | undefined;
}

export const EMPTY_OFFER_DRAFT: OfferDraft = { zoneId: 'MARKET', discountBp: undefined, perRideCapMinor: undefined, spendCapMinor: undefined, reasonCode: '', windowHours: undefined };

/** The first thing wrong with a proposal draft against the policy's published bounds, or undefined. The server re-checks every one of them. */
export function offerDraftIssue(d: OfferDraft, policy: Pick<ActiveIncentivesListView['riderDiscountPolicy'], 'maxDiscountBp' | 'maxDiscountPerRide' | 'maxWindowHours'>, reasons: readonly string[]): OfferDraftIssue | undefined {
  if (d.zoneId.trim() === '') return 'ZONE_REQUIRED';
  if (d.discountBp === undefined || !Number.isInteger(d.discountBp) || d.discountBp < 1) return 'DISCOUNT_REQUIRED';
  if (d.discountBp > policy.maxDiscountBp) return 'DISCOUNT_OVER_POLICY';
  if (d.perRideCapMinor === undefined || d.perRideCapMinor <= 0n) return 'PER_RIDE_CAP_REQUIRED';
  if (policy.maxDiscountPerRide !== null && /^\d+$/.test(policy.maxDiscountPerRide.amountMinor) && d.perRideCapMinor > BigInt(policy.maxDiscountPerRide.amountMinor)) return 'PER_RIDE_CAP_OVER_POLICY';
  if (d.spendCapMinor === undefined || d.spendCapMinor <= 0n) return 'SPEND_CAP_REQUIRED';
  if (!reasons.includes(d.reasonCode)) return 'REASON_REQUIRED';
  if (d.windowHours === undefined || d.windowHours > policy.maxWindowHours || !(OFFER_WINDOW_HOURS as readonly number[]).includes(d.windowHours)) return 'WINDOW_REQUIRED';
  return undefined;
}

/** The body of a proposal: the window starts now and runs `windowHours`; an actor or a currency is never part of it. */
export function offerProposalBody(marketId: string, d: OfferDraft, nowIso: string): { marketId: string; zoneId?: string; value: { discountBp: number; perRideCapMinor: string }; spendCapMinor: string; reasonCode: string; validFrom: string; validTo: string } {
  return {
    marketId,
    ...(d.zoneId !== 'MARKET' ? { zoneId: d.zoneId } : {}),
    value: { discountBp: d.discountBp ?? 0, perRideCapMinor: (d.perRideCapMinor ?? 0n).toString() },
    spendCapMinor: (d.spendCapMinor ?? 0n).toString(),
    reasonCode: d.reasonCode,
    validFrom: nowIso,
    validTo: new Date(Date.parse(nowIso) + (d.windowHours ?? 0) * 3_600_000).toISOString(),
  };
}

/** Basis points as the percentage the screen types and reads (`1500` -> `15`, `1250` -> `12.5`), never a float computation on money. */
export function bpToPercentText(bp: number): string {
  const whole = Math.floor(bp / 100);
  const frac = bp % 100;
  return frac === 0 ? String(whole) : `${whole}.${String(frac).padStart(2, '0').replace(/0$/, '')}`;
}

/** A typed percentage (`15`, `12.5`, `12,5`) as basis points, or undefined when it is not a number with at most two decimals. */
export function percentTextToBp(text: string): number | undefined {
  const m = /^(\d{1,3})(?:[.,](\d{1,2}))?$/.exec(text.trim());
  if (m === null) return undefined;
  return Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
}

/** How much of an offer's cap is used, in basis points (capped at 10 000). */
export function offerSpendBp(o: Pick<ActiveIncentiveView, 'spent' | 'spendCap'>): number {
  const cap = /^\d+$/.test(o.spendCap.amountMinor) ? BigInt(o.spendCap.amountMinor) : 0n;
  const spent = /^\d+$/.test(o.spent.amountMinor) ? BigInt(o.spent.amountMinor) : 0n;
  return cap <= 0n ? 10_000 : Math.min(10_000, Number((spent * 10_000n) / cap));
}
